import React, { useEffect, useMemo, useRef, useState } from "react";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { convertToRaw, ContentState, EditorState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";

import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { formatPhoneNumber, getTimezoneOffsetMinutes } from "@/utils/Utils.js";
import {
  DangerButton,
  PrimaryButton,
  SecondaryButton,
} from "@/components/Buttons.jsx";
import { addToast, getMerchants } from "@/store/app/action.js";
import { updateSellerInfo, uploadFile } from "@/api/index.js";
import { getStore, generateDeliveryPolygon, getDeliveryPolygonRadius } from "@/api/settings.js";
import Image from "@/images/user-avatar-80.png";
import config from "@/config";
import ToggleSwitch from "@/components/ToggleSwitch.jsx";
import DeliveryFee from "./DeliveryFee/index.jsx";
import Tooltip from "@/components/Tooltip.jsx";
import ModalBasic from "@/components/ModalBasic.jsx";
import { requestEmployeeDelivery } from "@/api/admin.js";
import Badge from "@/components/Badges.jsx";
import { FaCrown, FaLock, FaLockOpen } from "react-icons/fa6";
import { MdWorkspacePremium } from "react-icons/md"
import DeliveryMap from "@/components/DeliveryMap.jsx";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import CanView from "@/components/CanView.jsx";
import Transition from "@/utils/Transition";
import TabbedNavigation from "@/components/TabbedNavigation.jsx";
import StoreHoursPanel from "./StoreHoursPanel.jsx";
import SpecialHoursPanel from "./SpecialHoursPanel.jsx";
import {
  PAYMENT_LOGO_IDS,
  PAYMENT_LOGO_OPTIONS,
  buildStorePaymentLogoPayload,
  hydrateStorePaymentLogoState,
  paymentLogoExplicitBrandIds,
  stablePaymentLogoJson,
} from "@/utils/paymentLogos.js";

// Theme Dropdown Component
function ThemeDropdown({ options, value, onChange, disabled = false }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [dropdownOpen]);

  // Close if esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen]);

  const selectedOption = options.find((opt) => opt.value === value) || options[0] || {
    value: "classic",
    label: "Classic",
    color: "#14aa6e",
  };

  return (
    <div className="relative">
      <button
        ref={trigger}
        className="form-select w-full text-left"
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          if (disabled) return;
          setDropdownOpen(!dropdownOpen);
        }}
        type="button"
      >
        <span className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded-full border border-slate-300 dark:border-slate-600"
            style={{ backgroundColor: selectedOption.color }}
          />
          {selectedOption.label}
        </span>
      </button>
      <Transition
        show={dropdownOpen}
        tag="div"
        className="z-10 absolute top-full left-0 w-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded shadow-lg overflow-hidden mt-1"
        enter="transition ease-out duration-100 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div ref={dropdown} className="max-h-64 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`flex items-center w-full hover:bg-slate-50 hover:dark:bg-slate-600 py-2 px-3 cursor-pointer ${value === option.value
                ? "text-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
                : "text-slate-600 dark:text-slate-300"
                }`}
              onClick={(e) => {
                e.preventDefault();
                onChange(option.value);
                setDropdownOpen(false);
              }}
            >
              <svg
                className={`shrink-0 mr-2 fill-current text-indigo-500 ${value !== option.value && "invisible"
                  }`}
                width="12"
                height="9"
                viewBox="0 0 12 9"
              >
                <path d="M10.28.28L3.989 6.575 1.695 4.28A1 1 0 00.28 5.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28.28z" />
              </svg>
              <div
                className="w-4 h-4 rounded-full mr-2 border border-slate-300 dark:border-slate-600"
                style={{ backgroundColor: option.color }}
              />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      </Transition>
    </div>
  );
}

function StorePanel() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [amSoDirty, setAmSoDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");
  const { selectedLocation, selectedMerchant, user } = useSelector(
    (state) => state.app
  );
  const [fullLocationDetail, setFullLocationDetail] = useState(null);
  const [resetForm, setResetForm] = useState(false);
  const [resetDeliveryFee, setResetDeliveryFee] = useState(false);
  const [editorState, setEditorState] = useState(() => {
    return EditorState.createEmpty();
  });

  // Sync active tab with URL
  useEffect(() => {
    const pathname = location.pathname;
    if (pathname === "/settings/store/appearance") {
      setActiveTab("appearance");
    } else if (pathname === "/settings/store/hours" || pathname === "/settings/hours") {
      setActiveTab("hours");
    } else if (pathname === "/settings/store") {
      setActiveTab("settings");
    }
  }, [location.pathname]);

  // Refresh store data when any tab becomes active to get latest data
  useEffect(() => {
    if (activeTab && fullLocationDetail) {
      _getstore();
    }
  }, [activeTab]);

  // Handle tab change and update URL
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "appearance") {
      navigate("/settings/store/appearance");
    } else if (tabId === "hours") {
      navigate("/settings/store/hours");
    } else if (tabId === "settings") {
      navigate("/settings/store");
    }
  };

  const onEditorStateChange = (editorState) => {
    setEditorState(editorState);
    checkDirty();
  };

  const _getstore = async () => {
    const res = await getStore();
    setFullLocationDetail(res.store);
  };

  useEffect(() => {
    _getstore();
  }, [selectedLocation]);

  useEffect(() => {
    if (resetDeliveryFee) {
      setResetDeliveryFee(false);
    }
  }, [resetDeliveryFee]);

  const [deliveryFeeSettings, setDeliveryFeeSettings] = useState(
    fullLocationDetail?.delivery_fee_settings || []
  );
  const [originalFeeSettings, setOriginalFeeSettings] = useState(
    fullLocationDetail?.delivery_fee_settings || []
  );
  const [complianceFeeSetting, setComplianceFeeSetting] =
    useState("pass-through");

  const [cartwheelModal, setCartwheelModal] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState("dsp");
  const [immediateSyndication, setImmediateSyndication] = useState(false);
  const [deliveryExtraVerification, setDeliveryExtraVerification] = useState([]);
  const [maxDeliveryDistance, setMaxDeliveryDistance] = useState(0);
  const [proposedPolygon, setProposedPolygon] = useState(null);
  const [originalMaxDistance, setOriginalMaxDistance] = useState(10);
  const [isGeneratingPolygon, setIsGeneratingPolygon] = useState(false);
  const [pendingDistance, setPendingDistance] = useState(null);
  const [geojsonModal, setGeojsonModal] = useState(false);
  const [geojsonEditor, setGeojsonEditor] = useState("");
  const [polygonLocked, setPolygonLocked] = useState(false);
  const [theme, setTheme] = useState("classic");
  const [applyThemeToAllLocations, setApplyThemeToAllLocations] = useState(false);
  const [hidePaymentLogos, setHidePaymentLogos] = useState(false);
  const [useMothershipPaymentLogos, setUseMothershipPaymentLogos] =
    useState(false);
  const [selectedPaymentLogoIds, setSelectedPaymentLogoIds] = useState([]);

  // Theme options with colors
  const themeOptions = [
    { value: "classic", label: "Classic", color: "#14aa6e" },
    { value: "ocean", label: "Ocean", color: "#06b6d4" },
    { value: "warm", label: "Warm", color: "#f97316" },
    { value: "purple", label: "Purple", color: "#9333ea" },
    { value: "forest", label: "Forest", color: "#15803d" },
    { value: "sunset", label: "Sunset", color: "#f97316" },
    { value: "minimalist", label: "Minimalist", color: "#000000" },
    { value: "crimson", label: "Crimson", color: "#dc2626" },
    { value: "rose", label: "Rose", color: "#e11d48" },
    { value: "midnight", label: "Midnight", color: "#1e40af" },
  ];

  useEffect(() => {
    checkDirty();
  }, [
    deliveryFeeSettings,
    complianceFeeSetting,
    deliveryMethod,
    immediateSyndication,
    deliveryExtraVerification,
    maxDeliveryDistance,
    polygonLocked,
    theme,
    applyThemeToAllLocations,
    hidePaymentLogos,
    useMothershipPaymentLogos,
    selectedPaymentLogoIds,
  ]);
  useEffect(() => {
    setResetForm(true);
    const _deliveryFeeSettings = fullLocationDetail?.delivery_fee_settings
      ? JSON.parse(
        JSON.stringify(fullLocationDetail?.delivery_fee_settings)
      ) || {
        type: "pass-through",
        settings: [],
      }
      : {
        type: "pass-through",
        settings: [],
      };
    setDeliveryFeeSettings(_deliveryFeeSettings);
    setOriginalFeeSettings(_deliveryFeeSettings);
    setComplianceFeeSetting(
      fullLocationDetail?.compliance_fee_setting || "pass-through"
    );

    setDeliveryMethod(
      fullLocationDetail?.employee_delivery ? "employee" : "dsp"
    );
    setImmediateSyndication(fullLocationDetail?.immediate_syndication || false);
    const extraVer = fullLocationDetail?.delivery_extra_verification;
    setDeliveryExtraVerification(
      Array.isArray(extraVer) ? [...extraVer] : []
    );
    const initialDistance =
      fullLocationDetail?.direct_max_delivery_distance || 10;
    setMaxDeliveryDistance(initialDistance);
    setOriginalMaxDistance(initialDistance);
    setPolygonLocked(fullLocationDetail?.polygon_locked || false);

    // Initialize toggle based on whether merchant theme exists
    const shouldApplyToAll = !!(selectedMerchant?.theme);
    setApplyThemeToAllLocations(shouldApplyToAll);

    // Initialize theme from fullLocationDetail or merchant
    const locationTheme = fullLocationDetail?.theme || "classic";
    const merchantTheme = selectedMerchant?.theme || "classic";
    const initialTheme = shouldApplyToAll ? merchantTheme : locationTheme;
    setTheme(initialTheme || "classic");

    const contentBlock = htmlToDraft(
      fullLocationDetail?.description_html || ""
    );
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(
        contentBlock.contentBlocks
      );
      const editorState = EditorState.createWithContent(contentState);
      setEditorState(editorState);
    } else {
      setEditorState(EditorState.createEmpty());
    }

    const orgMS = (selectedMerchant?.locations?.length ?? 0) > 1;
    const paymentState = hydrateStorePaymentLogoState(
      fullLocationDetail?.payment_logos,
      orgMS,
      selectedMerchant?.payment_logos
    );
    setHidePaymentLogos(paymentState.hidden);
    setUseMothershipPaymentLogos(paymentState.useMothershipPaymentLogos);
    setSelectedPaymentLogoIds(paymentState.selectedIds);
  }, [fullLocationDetail, selectedMerchant]);
  useEffect(() => {
    if (resetForm) {
      setResetForm(false);
    }
  }, [resetForm]);

  // Update toggle when selectedMerchant changes
  useEffect(() => {
    if (selectedMerchant && fullLocationDetail) {
      const shouldApplyToAll = !!(selectedMerchant?.theme);
      setApplyThemeToAllLocations(shouldApplyToAll);
    }
  }, [selectedMerchant, fullLocationDetail]);

  // Update theme when toggle changes
  useEffect(() => {
    if (fullLocationDetail && selectedMerchant) {
      const locationTheme = fullLocationDetail?.theme || "classic";
      const merchantTheme = selectedMerchant?.theme || "classic";
      const newTheme = applyThemeToAllLocations ? merchantTheme : locationTheme;
      setTheme(newTheme || "classic");
    }
  }, [applyThemeToAllLocations, fullLocationDetail, selectedMerchant]);

  const iconUploadRef = React.createRef();
  const iconUrlRef = React.createRef();
  const iconDisplayRef = React.createRef();
  const bannerUploadRef = React.createRef();
  const bannerUrlRef = React.createRef();
  const bannerDisplayRef = React.createRef();

  const formRef = React.createRef();

  const orgHasMultipleStores = (selectedMerchant?.locations?.length ?? 0) > 1;

  const togglePaymentLogoId = React.useCallback(
    (id) => {
      setSelectedPaymentLogoIds((prev) => {
        if (!prev.includes(id)) {
          return [...prev, id];
        }
        const multiCustom =
          orgHasMultipleStores &&
          !useMothershipPaymentLogos &&
          !hidePaymentLogos;
        if (multiCustom && prev.length <= 1) {
          return prev;
        }
        return prev.filter((x) => x !== id);
      });
    },
    [
      orgHasMultipleStores,
      useMothershipPaymentLogos,
      hidePaymentLogos,
    ]
  );

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const keysToCheck = [
      "name",
      "phone",
      "email",
      "enable_delivery",
      "enable_pickup",
      // "icon",
      // "web_banners",
      "compliance_fee_setting",
      "direct_max_delivery_distance",
      "theme",
    ];
    const formData = new FormData(e.target);

    // Check if max delivery distance changed
    const maxDistanceChanged = !!pendingDistance;
    let polygonToSave = fullLocationDetail.polygon;

    // If max distance changed but no proposed polygon exists, generate it
    if (maxDistanceChanged && !proposedPolygon) {
      try {
        dispatch(
          addToast({
            type: "info",
            message: "Generating new delivery area...",
            timer: 3,
          })
        );
        const response = await generateDeliveryPolygon(maxDeliveryDistance);
        polygonToSave = response.polygon;
        setProposedPolygon(response.polygon);
      } catch (error) {
        dispatch(
          addToast({
            type: "error",
            message: "Failed to generate new delivery area",
            timer: 5,
          })
        );
        setLoading(false);
        return;
      }
    } else if (proposedPolygon) {
      // Use the proposed polygon if it exists
      polygonToSave = proposedPolygon;
    }

    // Get form values, falling back to fullLocationDetail when inputs aren't in DOM (different tab)
    const getFormValue = (key, defaultValue = null) => {
      const formValue = formData.get(key);
      if (formValue !== null && formValue !== undefined && formValue !== "") {
        return formValue;
      }
      return defaultValue !== null ? defaultValue : fullLocationDetail[key];
    };

    // Get icon from ref or form, fallback to fullLocationDetail
    const iconValue = iconUrlRef.current?.value || formData.get("icon") || fullLocationDetail.icon;

    // Get web_banners from ref or form, fallback to fullLocationDetail
    const webBannersValue = bannerUrlRef.current?.value || formData.get("web_banners") || fullLocationDetail.web_banners?.[0];
    const webBannersArray = webBannersValue ? [webBannersValue] : (fullLocationDetail.web_banners || []);

    // Get checkbox values directly from form elements (checkboxes don't appear in FormData when unchecked)
    const enableDeliveryCheckbox = e.target.elements.namedItem("enable_delivery");
    const enablePickupCheckbox = e.target.elements.namedItem("enable_pickup");
    const enableDelivery = enableDeliveryCheckbox ? enableDeliveryCheckbox.checked : fullLocationDetail.enable_delivery;
    const enablePickup = enablePickupCheckbox ? enablePickupCheckbox.checked : fullLocationDetail.enable_pickup;

    const data = {
      name: fullLocationDetail.name,
      phone: fullLocationDetail.phone,
      email: getFormValue("email", fullLocationDetail.email),
      enable_delivery: enableDelivery,
      enable_pickup: enablePickup,
      icon: iconValue,
      web_banners: webBannersArray,
      retailer_minimum: formData.get("retailer_minimum") !== null && formData.get("retailer_minimum") !== ""
        ? Number.parseFloat(formData.get("retailer_minimum"))
        : (fullLocationDetail.retailer_minimum || 0),
      delivery_fee_settings: deliveryFeeSettings,
      compliance_fee_setting: complianceFeeSetting,
      retailer_pickup_minimum: formData.get("retailer_pickup_minimum") !== null && formData.get("retailer_pickup_minimum") !== ""
        ? Number.parseFloat(formData.get("retailer_pickup_minimum"))
        : (fullLocationDetail.retailer_pickup_minimum || 0),
      employee_delivery: Boolean(
        deliveryMethod === "employee" && fullLocationDetail.cartwheel_team_id
      ),
      immediate_syndication: immediateSyndication,
      delivery_extra_verification:
        deliveryMethod === "dsp"
          ? deliveryExtraVerification.filter((x) =>
            x === "pincode" || x === "signature"
          )
          : [],
      direct_max_delivery_distance: Number.parseFloat(maxDeliveryDistance),
      polygon: polygonToSave,
      polygon_locked: polygonLocked,
      description_html: draftToHtml(
        convertToRaw(editorState.getCurrentContent())
      )
        .replace("<p></p>", "")
        .replace("\\n", ""),
      theme: theme || "classic",
      apply_theme_to_all_locations: applyThemeToAllLocations,
      payment_logos: buildStorePaymentLogoPayload({
        hidden: hidePaymentLogos,
        useMothershipPaymentLogos,
        selectedIds: selectedPaymentLogoIds,
        orgHasMultipleStores,
      }),
    };

    // Validate delivery distance
    if (
      data.direct_max_delivery_distance < 5 ||
      data.direct_max_delivery_distance > 20
    ) {
      dispatch(
        addToast({
          type: "error",
          message: "Maximum delivery distance must be between 5 and 20 miles",
          timer: 5,
        })
      );
      setLoading(false);
      return;
    }

    if (!keysToCheck.every((key) => data[key] !== undefined)) {
      // log each field name that's not filled out
      //log the keys that are not filled out
      dispatch(
        addToast({
          type: "error",
          message: "Please fill out all fields",
          timer: 5,
        })
      );
      setLoading(false);
      return;
    }

    try {
      await updateSellerInfo({ basic: data });
      dispatch(
        addToast({ type: "success", message: "Store updated", timer: 5 })
      );
      dispatch(getMerchants());
      await _getstore();
      setAmSoDirty(false);

      // Update original distance to current distance after successful save
      setOriginalMaxDistance(maxDeliveryDistance);
      setProposedPolygon(null);
      setPendingDistance(null);
    } catch (e) {
      let message = "please try again";
      try {
        const serverMessage = e.response?.data?.error;
        if (serverMessage) {
          message = serverMessage;
        }
      } catch { }

      dispatch(
        addToast({
          type: "error",
          message: "Error updating store " + message,
          timer: 5,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const changeDeliveryMode = (mode) => {
    if (mode === "dsp") {
      setDeliveryMethod("dsp");
      if (fullLocationDetail.employee_delivery) {
        setDeliveryFeeSettings({ type: "pass-through", settings: [] });
      } else {
        setDeliveryFeeSettings(originalFeeSettings);
      }
    } else {
      if (!fullLocationDetail.cartwheel_team_id) {
        setCartwheelModal("request");
      } else if (fullLocationDetail.cartwheel_team_id === "pending") {
        setCartwheelModal("pending");
      } else {
        setDeliveryMethod("employee");
        if (!fullLocationDetail.employee_delivery) {
          setDeliveryFeeSettings({ type: "fixed", amount: 5.99 });
        } else {
          setDeliveryFeeSettings(originalFeeSettings);
        }
      }
    }
    setResetDeliveryFee(true);
  };

  const generateNewPolygon = async () => {
    setIsGeneratingPolygon(true);
    try {
      const response = await generateDeliveryPolygon(maxDeliveryDistance);
      setProposedPolygon(response.polygon);
      setPendingDistance(null); // Clear pending distance after generation
      dispatch(
        addToast({
          type: "success",
          message: "New delivery area generated successfully",
          timer: 5,
        })
      );
    } catch (error) {
      dispatch(
        addToast({
          type: "error",
          message: "Failed to generate new delivery area",
          timer: 5,
        })
      );
    } finally {
      setIsGeneratingPolygon(false);
    }
  };

  const revertChanges = () => {
    setMaxDeliveryDistance(originalMaxDistance);
    setProposedPolygon(null);
    setPendingDistance(null);
    dispatch(
      addToast({
        type: "success",
        message: "Changes reverted to original settings",
        timer: 3,
      })
    );
  };

  const openGeojsonEditor = () => {
    const currentPolygon = proposedPolygon || fullLocationDetail.polygon;
    setGeojsonEditor(JSON.stringify(currentPolygon, null, 2));
    setGeojsonModal(true);
  };

  const saveGeojsonEdit = () => {
    try {
      const parsedPolygon = JSON.parse(geojsonEditor);
      // Basic validation - check if it's a valid GeoJSON polygon
      if (parsedPolygon.type === "Polygon" && parsedPolygon.coordinates) {
        setProposedPolygon(parsedPolygon);
        setGeojsonModal(false);
        dispatch(
          addToast({
            type: "success",
            message: "GeoJSON polygon updated successfully",
            timer: 5,
          })
        );
      } else {
        dispatch(
          addToast({
            type: "error",
            message: "Invalid GeoJSON polygon format",
            timer: 5,
          })
        );
      }
    } catch (error) {
      dispatch(
        addToast({
          type: "error",
          message: "Invalid JSON format",
          timer: 5,
        })
      );
    }
  };

  const checkDirty = () => {
    const keysToCheck = [
      "name",
      "phone",
      "email",
      "enable_delivery",
      "enable_pickup",
      "icon",
      "web_banners",
      "delivery_fee_settings",
      "retailer_minimum",
      "compliance_fee_setting",
      "retailer_pickup_minimum",
      "immediate_syndication",
      "theme",
    ];
    if (!formRef.current) {
      return;
    }
    const data = new FormData(formRef.current);
    const dirty = keysToCheck.some((key) => {
      let original = fullLocationDetail[key] || "";
      let updated = data.get(key) || "";
      if (key === "phone") {
        original = formatPhoneNumber(original);
        updated = formatPhoneNumber(updated);
      }
      if (key === "web_banners") {
        original = fullLocationDetail.web_banners?.[0];
        updated = data.get(key);
      }
      if (["enable_delivery", "enable_pickup"].includes(key)) {
        original = !!fullLocationDetail[key];
        // Get checkbox checked state directly from form element (checkboxes don't appear in FormData when unchecked)
        const checkbox = formRef.current.elements.namedItem(key);
        updated = checkbox ? checkbox.checked : false;
      }
      if (key === "delivery_fee_settings") {
        original = JSON.stringify(fullLocationDetail[key]);
        updated = JSON.stringify(deliveryFeeSettings);
      }
      if (key === "compliance_fee_setting") {
        original = fullLocationDetail[key];
        updated = complianceFeeSetting;
      }
      if (key === "immediate_syndication") {
        original = fullLocationDetail[key] || false;
        updated = immediateSyndication;
      }
      if (key === "direct_max_delivery_distance") {
        original = fullLocationDetail[key] || 10;
        updated = maxDeliveryDistance;
      }
      if (key === "polygon_locked") {
        original = fullLocationDetail[key] || false;
        updated = polygonLocked;
      }
      if (key === "theme") {
        const currentTheme = applyThemeToAllLocations
          ? (selectedMerchant?.theme || "classic")
          : (fullLocationDetail?.theme || "classic");
        original = currentTheme;
        updated = theme;
      }

      return original !== updated;
    });

    const descriptionHtmlCurrent = draftToHtml(
      convertToRaw(editorState.getCurrentContent())
    )
      .replace("<p></p>", "")
      .replace("\\n", "");
    const descriptionBaseline = fullLocationDetail.description_html || "";
    const descriptionDirty = descriptionHtmlCurrent !== descriptionBaseline;

    const normalizeDeliveryVerification = (v) =>
      JSON.stringify(
        [...(Array.isArray(v) ? v : [])]
          .filter((x) => x === "pincode" || x === "signature")
          .sort()
      );
    const effectiveVerification =
      deliveryMethod === "dsp" ? deliveryExtraVerification : [];
    const verificationDirty =
      normalizeDeliveryVerification(effectiveVerification) !==
      normalizeDeliveryVerification(
        fullLocationDetail.delivery_extra_verification
      );

    const paymentPayload = buildStorePaymentLogoPayload({
      hidden: hidePaymentLogos,
      useMothershipPaymentLogos,
      selectedIds: selectedPaymentLogoIds,
      orgHasMultipleStores,
    });
    const paymentLogoDirty =
      stablePaymentLogoJson(paymentPayload) !==
      stablePaymentLogoJson(fullLocationDetail.payment_logos);

    setAmSoDirty(
      dirty ||
        descriptionDirty ||
        verificationDirty ||
        paymentLogoDirty
    );
  };

  useEffect(() => {
    if (!formRef.current || !fullLocationDetail) return;
    checkDirty();
  }, [
    hidePaymentLogos,
    useMothershipPaymentLogos,
    selectedPaymentLogoIds,
  ]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    let checks = {};
    if (e.target.name === "icon") {
      checks = {
        minWidth: 100,
        width: 100,
        height: 100,
        minAspectRatio: 1,
        maxAspectRatio: 1.25,
      };
    } else if (e.target.name === "banner") {
      checks = {
        minHeight: 300,
        height: 300,
        minAspectRatio: 1.5,
        maxAspectRatio: 3,
      };
    }

    if (!file.type.startsWith("image/")) {
      dispatch(
        addToast({
          type: "error",
          message: "Please upload a valid image file",
          timer: 5,
        })
      );
      return;
    }
    const img = document.createElement("img");
    img.onload = () => {
      if (checks.minWidth) {
        if (img.width < checks.minWidth) {
          dispatch(
            addToast({
              type: "error",
              message:
                "Please upload an image at least " +
                checks.minWidth +
                "px wide",
              timer: 10,
            })
          );
          return;
        }
      }
      if (checks.minHeight) {
        if (img.height < checks.minHeight) {
          dispatch(
            addToast({
              type: "error",
              message:
                "Please upload an image at least " +
                checks.minHeight +
                "px tall",
              timer: 10,
            })
          );
          return;
        }
      }
      // Check aspect ratio
      if (checks.minAspectRatio || checks.maxAspectRatio) {
        if (
          img.width / img.height < checks.minAspectRatio ||
          img.width / img.height > checks.maxAspectRatio
        ) {
          dispatch(
            addToast({
              type: "error",
              message:
                "Please upload an image an aspect ratio between " +
                checks.minAspectRatio +
                ":1 and " +
                checks.maxAspectRatio +
                ":1",
              timer: 10,
            })
          );
          return;
        }
      }

      // Resize image
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (checks.width && checks.height) {
        canvas.width = checks.width;
        canvas.height = checks.height;
      } else if (checks.width && !checks.height) {
        canvas.width = checks.width;
        canvas.height = img.height * (checks.width / img.width);
      } else if (!checks.width && checks.height) {
        canvas.height = checks.height;
        canvas.width = img.width * (checks.height / img.height);
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob and upload
      canvas.toBlob(async (blob) => {
        try {
          try {
            const response = await uploadFile(blob);
            switch (e.target.name) {
              case "icon":
                iconUrlRef.current.value = response.url;
                iconDisplayRef.current.src = response.url;
                break;
              case "banner":
                bannerUrlRef.current.value = response.url;
                bannerDisplayRef.current.src = response.url;
                break;
            }
            checkDirty();
          } catch (e) { }
        } catch (error) { }
      }, file.type);
    };

    // Read the file as a URL to create an image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  if (!fullLocationDetail) return null;
  if (resetForm) return null;
  return (
    <div className="grow">
      {/* Panel body */}
      <input
        type="file"
        accept="image/*"
        ref={iconUploadRef}
        onChange={handleFileChange}
        name="icon"
        style={{ display: "none" }} // Hide the file input
      />
      <input
        type="file"
        accept="image/*"
        ref={bannerUploadRef}
        onChange={handleFileChange}
        name="banner"
        style={{ display: "none" }} // Hide the file input
      />

      <ModalBasic
        modalOpen={cartwheelModal}
        setModalOpen={setCartwheelModal}
        width="2xl"
        title={
          cartwheelModal === "request" ||
            cartwheelModal === "submitting" ||
            cartwheelModal === "submitted"
            ? "Request employee delivery"
            : "Employee delivery request pending"
        }
      >
        {cartwheelModal === "request" && (
          <div>
            <div className="flex flex-col gap-4">
              <div>
                <p>
                  In order to deliver orders to your customers using your own
                  employee, you will need to request Saucey to enable this
                  feature for your store. Saucey will provision the software and
                  credentials necessary for your employees to deliver orders.{" "}
                </p>
              </div>
              <div>
                <p>
                  The cost to enable this feature is free, but there is a $0.25
                  fee per order that is collected by Saucey. You will be able to
                  set the rate you charge your customers for delivery.
                </p>
              </div>
              <div>
                <p>
                  To proceed, submit the request below. You'll be notified once
                  the request is approved, then you can set your delivery rate.
                </p>
              </div>
            </div>
            <footer>
              <div className="flex flex-col px-6 py-5 border-t border-slate-200 dark:border-slate-700">
                <div className="flex self-end">
                  <PrimaryButton
                    loading={cartwheelModal === "submitting"}
                    onClick={async () => {
                      setCartwheelModal("submitting");
                      await requestEmployeeDelivery();
                      setCartwheelModal("submitted");
                    }}
                  >
                    Request Employee Delivery Feature
                  </PrimaryButton>
                </div>
              </div>
            </footer>
          </div>
        )}
        {cartwheelModal === "submitted" && (
          <div>
            <div className="p-6">
              <div className="flex flex-col gap-4">
                <div>
                  <p>
                    Your request for the employee delivery feature has been
                    submitted. You will be notified when your request is
                    approved.
                  </p>
                </div>
              </div>
            </div>
            <footer>
              <div className="flex flex-col px-6 py-5 border-t border-slate-200 dark:border-slate-700">
                <div className="flex self-end">
                  <PrimaryButton
                    onClick={() => {
                      dispatch(getMerchants());
                      setCartwheelModal(null);
                    }}
                  >
                    Close
                  </PrimaryButton>
                </div>
              </div>
            </footer>
          </div>
        )}

        {cartwheelModal === "pending" && (
          <div>
            <div className="p-6">
              <div className="flex flex-col gap-4">
                <div>
                  <p>
                    Your request for the employee delivery feature is pending.
                    You will be notified when your request is approved.
                  </p>
                </div>
              </div>
            </div>
            <footer>
              <div className="flex flex-col px-6 py-5 border-t border-slate-200 dark:border-slate-700">
                <div className="flex self-end">
                  <PrimaryButton
                    onClick={() => {
                      setCartwheelModal(null);
                    }}
                  >
                    Close
                  </PrimaryButton>
                </div>
              </div>
            </footer>
          </div>
        )}
      </ModalBasic>

      <form onSubmit={submit} onChange={checkDirty} ref={formRef}>
        <div className="p-6">
          <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-5">
            My Store - {fullLocationDetail.name}
          </h2>

          {/* Tab Navigation */}
          <div className="mb-6">
            <TabbedNavigation
              tabs={[
                { id: "settings", label: "Store Settings" },
                { id: "appearance", label: "Store Appearance" },
                { id: "hours", label: "Store Hours" },
              ]}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              variant="underline"
              size="md"
            />
          </div>

          {/* Tab Content */}
          <div className="space-y-12">
            {/* Store Appearance Tab */}
            {activeTab === "appearance" && (
              <>
                <div className="space-y-12">
                  <section>
                    <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                      Store Logo
                    </h2>
                    <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                      <div className="mr-4">
                        <img
                          className="w-20 h-20"
                          src={fullLocationDetail.icon || Image}
                          ref={iconDisplayRef}
                          width="80"
                          height="80"
                          alt="User upload"
                        />
                        <input
                          name="icon"
                          ref={iconUrlRef}
                          className="form-input w-full"
                          type="text"
                          style={{ display: "none" }}
                          defaultValue={fullLocationDetail.icon}
                        />
                      </div>
                      <PrimaryButton
                        size={"sm"}
                        onClick={(e) => {
                          e.preventDefault();
                          iconUploadRef.current.click();
                        }}
                      >
                        Change
                      </PrimaryButton>
                    </div>
                  </section>
                  <section>
                    <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                      Header Image
                    </h2>
                    <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                      <div className="mr-4">
                        <img
                          className="w-80 h-40"
                          ref={bannerDisplayRef}
                          src={fullLocationDetail.web_banners?.[0] || Image}
                          width="160"
                          height="80"
                          alt="User upload"
                        />
                        <input
                          name="web_banners"
                          ref={bannerUrlRef}
                          className="form-input w-full"
                          type="text"
                          style={{ display: "none" }}
                          defaultValue={fullLocationDetail.web_banners?.[0]}
                        />
                      </div>
                      <DangerButton
                        size={"sm"}
                        onClick={(e) => {
                          e.preventDefault();
                          bannerUrlRef.current.value = "";
                          bannerDisplayRef.current.src = Image;
                        }}
                      >
                        Remove
                      </DangerButton>
                      <PrimaryButton
                        size={"sm"}
                        onClick={(e) => {
                          e.preventDefault();
                          bannerUploadRef.current.click();
                        }}
                      >
                        Change
                      </PrimaryButton>
                    </div>
                  </section>
                  <section>
                    <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                      Store Description
                    </h2>
                    <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                      <div className="w-full">
                        <Editor
                          editorState={editorState}
                          toolbarClassName="toolbarClassName"
                          wrapperClassName="w-3/4  h-48"
                          editorClassName="form-input w-full"
                          onEditorStateChange={onEditorStateChange}
                          toolbar={{
                            options: ["inline", "list"],
                            inline: {
                              inDropdown: false,
                              options: [
                                "bold",
                                "italic",
                                "underline",
                                "superscript",
                                "subscript",
                              ],
                            },
                            list: { inDropdown: false },
                            textAlign: { inDropdown: false, options: [] },
                            link: { inDropdown: false, options: [], component: null },
                            image: {
                              uploadEnabled: false,
                              options: [],
                              component: null,
                            },
                            embedded: { options: [], component: null },
                            remove: { className: undefined, component: undefined },

                            history: {
                              inDropdown: false,
                              options: [],
                              component: null,
                            },
                          }}
                        />
                      </div>
                    </div>
                  </section>
                  <section>
                    <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                      Website Theme
                    </h2>
                    <div className="sm:flex sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                      <div className="sm:w-1/3">
                        <label
                          className="block text-sm font-medium mb-1"
                          htmlFor="theme"
                        >
                          Select Theme
                        </label>
                        <ThemeDropdown
                          options={themeOptions}
                          value={theme}
                          onChange={(value) => {
                            setTheme(value);
                          }}
                        />
                      </div>
                    </div>
                    {selectedMerchant?.locations &&
                      selectedMerchant.locations.length > 1 && (
                        <div className="mt-4">
                          <ToggleSwitch
                            checked={applyThemeToAllLocations}
                            onChange={(checked) => {
                              setApplyThemeToAllLocations(checked);
                            }}
                            name="apply_theme_to_all_locations"
                            onLabel={`Apply to all ${selectedMerchant.name}'s locations`}
                            offLabel={`Apply to all ${selectedMerchant.name}'s locations`}
                          />
                        </div>
                      )}
                  </section>
                </div>

                <section className="mt-8">
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Payment logos
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 max-w-2xl">
                    {orgHasMultipleStores
                      ? "Use the same card logos as your main site, or pick different ones for this location."
                      : "Choose which card networks appear in the footer for this store."}
                  </p>
                  <ToggleSwitch
                    checked={!hidePaymentLogos}
                    onChange={(showLogos) => {
                      if (!showLogos) {
                        setHidePaymentLogos(true);
                        return;
                      }
                      setHidePaymentLogos(false);
                      if (orgHasMultipleStores) {
                        setUseMothershipPaymentLogos(true);
                        setSelectedPaymentLogoIds(
                          paymentLogoExplicitBrandIds(
                            selectedMerchant?.payment_logos
                          )
                        );
                      } else {
                        setUseMothershipPaymentLogos(false);
                        setSelectedPaymentLogoIds([]);
                      }
                    }}
                    name="show_payment_logos"
                    onLabel="Show payment method logos in footer"
                    offLabel="Show payment method logos in footer"
                  />
                  {orgHasMultipleStores && !hidePaymentLogos && (
                    <div className="mt-5">
                      <ToggleSwitch
                        checked={useMothershipPaymentLogos}
                        onChange={(checked) => {
                          setUseMothershipPaymentLogos(checked);
                          const fromMerchant = paymentLogoExplicitBrandIds(
                            selectedMerchant?.payment_logos
                          );
                          if (checked) {
                            setSelectedPaymentLogoIds(fromMerchant);
                          } else {
                            setSelectedPaymentLogoIds(
                              fromMerchant.length > 0
                                ? fromMerchant
                                : [...PAYMENT_LOGO_IDS]
                            );
                          }
                        }}
                        name="use_mothership_payment_logos"
                        onLabel="Use mothership payment logo settings"
                        offLabel="Use mothership payment logo settings"
                      />
                    </div>
                  )}
                  {!hidePaymentLogos &&
                    (!orgHasMultipleStores || !useMothershipPaymentLogos) && (
                      <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3">
                        {PAYMENT_LOGO_OPTIONS.map(({ id, label }) => (
                          <label
                            key={id}
                            className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              className="form-checkbox text-indigo-500"
                              checked={selectedPaymentLogoIds.includes(id)}
                              onChange={() => togglePaymentLogoId(id)}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    )}
                </section>
              </>
            )}

            {/* Store Settings Tab */}
            {activeTab === "settings" && (
              <>
                {/* Email */}
                <section>
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Primary Email
                  </h2>
                  <div className="flex flex-wrap mt-5">
                    <div className="mr-2">
                      <label
                        className="block text-sm font-medium mb-1"
                        htmlFor="location"
                      >
                        This is the email address where you will receive general
                        communications regarding this location
                      </label>
                      <input
                        id="location"
                        className="form-input w-[30rem]"
                        type="text"
                        defaultValue={fullLocationDetail.email}
                        name={"email"}
                      />
                    </div>
                  </div>
                </section>
                {selectedMerchant?.locations.length > 1 && (
                  <section>
                    <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                      Mothership URL
                    </h2>
                    <div className="flex gap-4">
                      <input
                        disabled={true}
                        className="form-input w-full"
                        type="text"
                        defaultValue={
                          config.direct_web_prefix +
                          (selectedMerchant.direct_web_url ||
                            selectedMerchant.easy_id)
                        }
                      />
                      <SecondaryButton
                        onClick={() => {
                          navigator.clipboard.writeText(
                            config.direct_web_prefix +
                            (selectedMerchant.direct_web_url ||
                              selectedMerchant.easy_id)
                          );
                          dispatch(
                            addToast({
                              message: "Copied to clipboard",
                              type: "success",
                              timer: 2,
                            })
                          );
                        }}
                      >
                        Copy
                      </SecondaryButton>
                      <PrimaryButton
                        onClick={() => {
                          window.open(
                            config.direct_web_prefix +
                            (selectedMerchant.direct_web_url ||
                              selectedMerchant.easy_id),
                            "_blank"
                          );
                        }}
                      >
                        Go
                      </PrimaryButton>
                    </div>
                    {Boolean(
                      (selectedMerchant.custom_direct_web_urls || []).length
                    ) && (
                        <div className="mt-2">
                          <span className="font-bold mr-2">
                            Custom URL
                            {selectedMerchant.custom_direct_web_urls.length > 1 && "s"}
                          </span>
                          <Badge color="green">
                            <div className="flex items-center gap-1" size="xs">
                              <FaCrown className="text-sm" />
                              Growth Subscription: Active
                            </div>
                          </Badge>
                        </div>
                      )}

                    {(selectedMerchant.custom_direct_web_urls || []).map((url, i) => (
                      <div className="flex gap-4 mt-2" key={"msurl" + i}>
                        <input
                          disabled={true}
                          className="form-input w-full"
                          type="text"
                          defaultValue={"https://" + url}
                        />
                        <SecondaryButton
                          onClick={() => {
                            navigator.clipboard.writeText("https://" + url);
                            dispatch(
                              addToast({
                                message: "Copied to clipboard",
                                type: "success",
                                timer: 2,
                              })
                            );
                          }}
                        >
                          Copy
                        </SecondaryButton>
                        <PrimaryButton
                          onClick={() => {
                            window.open("https://" + url);
                          }}
                        >
                          Go
                        </PrimaryButton>
                      </div>
                    ))}
                  </section>
                )}

                <section>
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Store URL
                  </h2>
                  <div className="flex gap-4">
                    <input
                      disabled={true}
                      className="form-input w-full"
                      type="text"
                      defaultValue={
                        config.direct_web_prefix +
                        (fullLocationDetail.direct_web_url ||
                          fullLocationDetail.store_id)
                      }
                    />
                    <SecondaryButton
                      onClick={() => {
                        navigator.clipboard.writeText(
                          config.direct_web_prefix +
                          (fullLocationDetail.direct_web_url ||
                            fullLocationDetail.store_id)
                        );
                        dispatch(
                          addToast({
                            message: "Copied to clipboard",
                            type: "success",
                            timer: 2,
                          })
                        );
                      }}
                    >
                      Copy
                    </SecondaryButton>
                    <PrimaryButton
                      onClick={() => {
                        window.open(
                          config.direct_web_prefix +
                          (fullLocationDetail.direct_web_url ||
                            fullLocationDetail.store_id),
                          "_blank"
                        );
                      }}
                    >
                      Go
                    </PrimaryButton>
                  </div>
                  {Boolean(
                    (fullLocationDetail.custom_direct_web_urls || []).length
                  ) && (
                      <div className="mt-2">
                        <span className="font-bold mr-2">
                          Custom URL
                          {fullLocationDetail.custom_direct_web_urls.length > 1 && "s"}
                        </span>
                        <Badge color="green">
                          <div className="flex items-center gap-1" size="xs">
                            <MdWorkspacePremium className="text-sm" />
                            Growth Subscription: Active
                          </div>
                        </Badge>
                      </div>
                    )}

                  {(fullLocationDetail.custom_direct_web_urls || []).map((url, i) => (
                    <div className="flex gap-4 mt-2" key={"url" + i}>
                      <input
                        disabled={true}
                        className="form-input w-full"
                        type="text"
                        defaultValue={
                          "https://" + fullLocationDetail.custom_direct_web_urls[i]
                        }
                      />
                      <SecondaryButton
                        onClick={() => {
                          navigator.clipboard.writeText(
                            "https://" + fullLocationDetail.custom_direct_web_urls[i]
                          );
                          dispatch(
                            addToast({
                              message: "Copied to clipboard",
                              type: "success",
                              timer: 2,
                            })
                          );
                        }}
                      >
                        Copy
                      </SecondaryButton>
                      <PrimaryButton
                        onClick={() => {
                          window.open(
                            "https://" + fullLocationDetail.custom_direct_web_urls[i]
                          );
                        }}
                      >
                        Go
                      </PrimaryButton>
                    </div>
                  ))}
                </section>
                <section>
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Shopping Settings
                  </h2>

                  <div className="flex space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                    <div className="flex gap-4 flex-col">
                      <div className="flex gap-4  items-center">
                        <div className="w-32">
                          <ToggleSwitch
                            checked={fullLocationDetail.enable_pickup}
                            name={"enable_pickup"}
                            onLabel={"Pickup"}
                            offLabel={"Pickup"}
                          />
                        </div>

                        <div>
                          <label
                            className="block text-sm font-medium mb-1"
                            htmlFor="retailer_pickup_minimum"
                          >
                            Order Minimum (Enter 0 for no limit)
                          </label>
                          <input
                            id="retailer_pickup_minimum"
                            className="form-input w-24"
                            type="number"
                            defaultValue={
                              fullLocationDetail.retailer_pickup_minimum || 0
                            }
                            name={"retailer_pickup_minimum"}
                          />
                        </div>
                      </div>
                      <div className="flex gap-4 items-center">
                        <div className="w-32">
                          <ToggleSwitch
                            checked={fullLocationDetail.enable_delivery}
                            name={"enable_delivery"}
                            onLabel={"Delivery"}
                            offLabel={"Delivery"}
                          />
                        </div>
                        <div>
                          <label
                            className="block text-sm font-medium mb-1"
                            htmlFor="order_minimum"
                          >
                            Order Minimum (Enter 0 for no limit)
                          </label>
                          <input
                            id="order_minimum"
                            className="form-input w-24"
                            type="number"
                            defaultValue={fullLocationDetail.retailer_minimum || 0}
                            name={"retailer_minimum"}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
                <section>
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Compliance Fee Settings
                  </h2>
                  <div>
                    Choose whether you want to absorb the $2 compliance fee or pass it
                    on to the customer
                  </div>

                  <div className="flex space-y-4 mt-5 flex-col">
                    <div className="sm:w-1/3 flex gap-4">
                      {/*  two toggle switches, one says "Retailer paid", the other says "Customer paid", when one is toggled, the other one untoggles*/}
                      <ToggleSwitch
                        checked={complianceFeeSetting === "absorbed"}
                        onChange={(checked) => {
                          if (checked) {
                            setComplianceFeeSetting("absorbed");
                          } else {
                            setComplianceFeeSetting("pass-through");
                          }
                        }}
                        name={"retailer-paid"}
                        onLabel={"Retailer Paid"}
                        offLabel={"Retailer Paid"}
                      />
                      <ToggleSwitch
                        checked={complianceFeeSetting !== "absorbed"}
                        onChange={(checked) => {
                          if (checked) {
                            setComplianceFeeSetting("pass-through");
                          } else {
                            setComplianceFeeSetting("absorbed");
                          }
                        }}
                        name={"customer-paid"}
                        onLabel={"Customer Paid"}
                        offLabel={"Customer Paid"}
                      />
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Delivery Settings
                  </h2>
                  <div>Choose how to deliver orders to your customers</div>

                  <div className="flex space-y-4 mt-5 flex-col">
                    <div className="flex flex-col gap-4">
                      {/* Delivery Method Selection */}
                      <ToggleSwitch
                        onLabel={
                          <div>
                            DSP
                            <div className="text-sm">
                              (Saucey sends out a driver from a third party delivery
                              service provider)
                            </div>
                          </div>
                        }
                        offLabel={
                          <div>
                            DSP
                            <div className="text-sm">
                              (Saucey sends out a driver from a third party delivery
                              service provider)
                            </div>
                          </div>
                        }
                        name="delivery_fee"
                        checked={deliveryMethod === "dsp"}
                        onChange={(e) => {
                          changeDeliveryMode("dsp");
                        }}
                      />
                      {deliveryMethod === "dsp" && (
                        <div className="ml-8 mt-2 flex flex-col gap-4">
                          <ToggleSwitch
                            onLabel={
                              <div>
                                Immediate Syndication
                                <div className="text-sm">
                                  Send driver out before clerk accepts the order
                                </div>
                              </div>
                            }
                            offLabel={
                              <div>
                                Immediate Syndication
                                <div className="text-sm">
                                  Send driver out before clerk accepts the order
                                </div>
                              </div>
                            }
                            name="immediate_syndication"
                            checked={immediateSyndication}
                            onChange={(checked) => {
                              setImmediateSyndication(checked);
                            }}
                          />
                          <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                            Additional delivery verification: when enabled, these
                            steps are requested at delivery only if your DSP supports
                            them.
                          </p>
                          <ToggleSwitch
                            onLabel={
                              <div>
                                PIN verification
                                <div className="text-sm">
                                  Courier will request a delivery PIN that's shown on the order tracking pagefrom the customer upon completion of the delivery.
                                </div>
                              </div>
                            }
                            offLabel={
                              <div>
                                PIN verification
                                <div className="text-sm">
                                  Courier will request a delivery PIN that's shown on the order tracking pagefrom the customer upon completion of the delivery.
                                </div>
                              </div>
                            }
                            name="delivery_verification_pincode"
                            checked={deliveryExtraVerification.includes("pincode")}
                            onChange={(checked) => {
                              setDeliveryExtraVerification((prev) => {
                                const next = new Set(
                                  Array.isArray(prev) ? prev : []
                                );
                                if (checked) next.add("pincode");
                                else next.delete("pincode");
                                return [...next].sort();
                              });
                            }}
                          />
                          <ToggleSwitch
                            onLabel={
                              <div>
                                Signature
                                <div className="text-sm">
                                  Courier will collect the signature of the customer upon completion of the delivery.
                                  DSP
                                </div>
                              </div>
                            }
                            offLabel={
                              <div>
                                Signature
                                <div className="text-sm">
                                  Courier will collect the signature of the customer upon completion of the delivery.
                                </div>
                              </div>
                            }
                            name="delivery_verification_signature"
                            checked={deliveryExtraVerification.includes("signature")}
                            onChange={(checked) => {
                              setDeliveryExtraVerification((prev) => {
                                const next = new Set(
                                  Array.isArray(prev) ? prev : []
                                );
                                if (checked) next.add("signature");
                                else next.delete("signature");
                                return [...next].sort();
                              });
                            }}
                          />
                        </div>
                      )}
                      <ToggleSwitch
                        onLabel={
                          <div>
                            Employee Delivery{" "}
                            <div className="text-sm flex">
                              (You will send an employee to deliver the order)
                              <Tooltip className="ml-2" bg="dark" size="md">
                                <div className="text-sm text-slate-200">
                                  Saucey charges $0.25 per delivery, you can set the
                                  rate you charge your customer. Saucey will provide
                                  the compliance technology to dispatch and deliver
                                  the order
                                </div>
                              </Tooltip>
                            </div>
                          </div>
                        }
                        offLabel={
                          <div>
                            Employee Delivery{" "}
                            {cartwheelModal === "submitted" ||
                              fullLocationDetail.cartwheel_team_id === "pending" ? (
                              "(Request Pending)"
                            ) : fullLocationDetail.cartwheel_team_id ? (
                              `(Available)`
                            ) : (
                              <PrimaryButton
                                type={"button"}
                                size={"sm"}
                                onClick={() => setCartwheelModal("request")}
                              >
                                Request
                              </PrimaryButton>
                            )}
                            <div className="text-sm flex">
                              (You will send an employee to deliver the order)
                              <Tooltip className="ml-2" bg="dark" size="md">
                                <div className="text-sm text-slate-200">
                                  Saucey charges $0.25 per delivery, you can set the
                                  rate you charge your customer. Saucey will provide
                                  the compliance technology to dispatch and deliver
                                  the order
                                </div>
                              </Tooltip>
                            </div>
                          </div>
                        }
                        name="delivery_fee"
                        checked={deliveryMethod === "employee"}
                        onChange={(e) => {
                          changeDeliveryMode("employee");
                        }}
                      />
                    </div>

                    {/* Maximum Delivery Distance */}
                    <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                      <div className="sm:w-1/3">
                        <label
                          className="block text-sm font-medium mb-1"
                          htmlFor="direct_max_delivery_distance"
                        >
                          Maximum Delivery Distance (miles)
                        </label>
                        <input
                          id="direct_max_delivery_distance"
                          className="form-input w-24"
                          type="number"
                          min="5"
                          max="20"
                          step="0.1"
                          value={maxDeliveryDistance}
                          disabled={polygonLocked && user.type !== "saucey_admin"}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            setMaxDeliveryDistance(value);
                            // Track pending distance if it differs from original
                            if (value !== originalMaxDistance) {
                              setPendingDistance(value);
                            } else {
                              setPendingDistance(null);
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseFloat(e.target.value);
                            if (value < 5) {
                              setMaxDeliveryDistance(5);
                            } else if (value > 20) {
                              setMaxDeliveryDistance(20);
                            }
                          }}
                        />
                        <div className="text-sm text-gray-500 mt-1">
                          Must be between 5 and 20 miles
                          {polygonLocked && user.type !== "saucey_admin" && (
                            <span className="text-red-500 ml-2">
                              (Locked by admin)
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Admin Lock Toggle */}
                      <CanView permissions="saucey_admin">
                        <div className="mt-4">
                          <ToggleSwitch
                            checked={polygonLocked}
                            onChange={(checked) => {
                              setPolygonLocked(checked);
                            }}
                            name="polygon_locked"
                            onLabel={
                              <div className="flex items-center gap-2">
                                <FaLock className="text-red-500" />
                                Lock GeoJSON
                              </div>
                            }
                            offLabel={
                              <div className="flex items-center gap-2">
                                <FaLockOpen className="text-green-500" />
                                Lock GeoJSON
                              </div>
                            }
                          />
                          <div className="text-sm text-gray-500 mt-1">
                            When locked, non-admin users cannot modify delivery distance
                          </div>
                        </div>
                      </CanView>

                      {/* Action Buttons */}
                      <div className="flex gap-3 mt-4">
                        <PrimaryButton
                          type="button"
                          size="sm"
                          disabled={!pendingDistance || isGeneratingPolygon}
                          loading={isGeneratingPolygon}
                          onClick={generateNewPolygon}
                        >
                          Generate Delivery Area Map
                        </PrimaryButton>
                        <CanView permissions="saucey_admin">
                          <PrimaryButton
                            type="button"
                            size="sm"
                            loading={isGeneratingPolygon}
                            onClick={generateNewPolygon}
                          >
                            <div className="flex items-center gap-2">
                              <FaCrown className="text-yellow-500" /> Force Generate
                              New Delivery Area Map
                            </div>
                          </PrimaryButton>
                        </CanView>

                        {proposedPolygon && (
                          <SecondaryButton
                            type="button"
                            size="sm"
                            onClick={revertChanges}
                          >
                            Revert Changes
                          </SecondaryButton>
                        )}
                      </div>
                      {/* Admin GeoJSON Editor Button */}
                      <CanView permissions="saucey_admin">
                        <div className="mt-3">
                          <SecondaryButton
                            type="button"
                            size="sm"
                            onClick={openGeojsonEditor}
                          >
                            <div className="flex items-center gap-2">
                              <FaCrown className="text-yellow-500" />
                              Manually Edit GeoJSON
                            </div>
                          </SecondaryButton>
                        </div>
                      </CanView>
                    </div>
                  </div>
                </section>

                {fullLocationDetail?.location && fullLocationDetail?.polygon && (
                  <section>
                    <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                      Delivery Area Map
                    </h2>
                    <div className="mt-5">
                      <DeliveryMap
                        location={fullLocationDetail.location}
                        polygon={fullLocationDetail.polygon}
                        proposedPolygon={proposedPolygon}
                      />
                    </div>
                  </section>
                )}

                <section className="relative">
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Delivery Fee Settings
                  </h2>
                  {!resetDeliveryFee && (
                    <DeliveryFee
                      settings={deliveryFeeSettings}
                      setSettings={(value) => {
                        setDeliveryFeeSettings(value);
                      }}
                      deliveryMethod={deliveryMethod}
                      maxDeliveryDistance={maxDeliveryDistance}
                    />
                  )}
                  {deliveryFeeSettings.type === "platform-absorbed" &&
                    user.type !== "saucey_admin" && (
                      <div className="absolute top-0 right-0 w-full h-full bg-slate-900/60 flex justify-center items-center">
                        <div className="p-2 bg-rose-50 dark:bg-rose-400/30 text-rose-500 dark:text-rose-400 text-lg">
                          Saucey is currently paying for delivery fees at this
                          location due to an ongoing promotion.
                        </div>
                      </div>
                    )}
                </section>
              </>
            )}

            {/* Store Hours Tab */}
            {activeTab === "hours" && (
              <>
                <div className="space-y-8">
                  <StoreHoursPanel
                    tzOffset={getTimezoneOffsetMinutes(fullLocationDetail?.tz)}
                    storeDetails={fullLocationDetail}
                  />
                  <SpecialHoursPanel
                    tzOffset={getTimezoneOffsetMinutes(fullLocationDetail?.tz)}
                    storeDetails={fullLocationDetail}
                    onRefresh={_getstore}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        {/* Panel footer */}
        <footer>
          <div className="flex flex-col px-6 py-5 border-t border-slate-200 dark:border-slate-700">
            <div className="flex self-end">
              {activeTab !== "hours" && (
                <PrimaryButton
                  type="submit"
                  disabled={!amSoDirty}
                  loading={loading}
                >
                  Save Changes
                </PrimaryButton>
              )}
            </div>
          </div>
        </footer>
      </form>

      {/* GeoJSON Editor Modal */}
      <ModalBasic
        modalOpen={geojsonModal}
        setModalOpen={setGeojsonModal}
        width="4xl"
        title="Edit GeoJSON Polygon"
      >
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              GeoJSON Polygon
            </label>
            <textarea
              className="form-input w-full h-96 font-mono text-sm"
              value={geojsonEditor}
              onChange={(e) => setGeojsonEditor(e.target.value)}
              placeholder="Enter valid GeoJSON polygon..."
            />
          </div>
          <div className="text-sm text-gray-500 mb-4">
            <strong>Note:</strong> This will replace the current polygon. Make sure the GeoJSON is valid.
          </div>
        </div>
        <footer>
          <div className="flex flex-col px-6 py-5 border-t border-slate-200 dark:border-slate-700">
            <div className="flex self-end gap-3">
              <SecondaryButton
                onClick={() => setGeojsonModal(false)}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={saveGeojsonEdit}
              >
                Save Changes
              </PrimaryButton>
            </div>
          </div>
        </footer>
      </ModalBasic>
    </div>
  );
}

export default StorePanel;
